import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';
import { Drawer } from 'vaul';
import { IconX } from '../icons';
import { IconButton } from './Button';

// Selector matching every focusable descendant inside a dialog. Used by
// the focus trap to find the first/last tabbable elements so Tab + Shift+Tab
// can cycle within the modal without escaping to the page behind.
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

// Tailwind's `md` breakpoint. Below this we render a Vaul bottom-sheet
// drawer (feels native on phones — drag-to-dismiss, sticks to bottom).
// At/above we render a centered dialog with overlay fade + scale entry.
const MOBILE_BREAKPOINT = 768;

// Snapshot the breakpoint once when the modal opens. We deliberately do
// NOT subscribe to viewport changes — if a user resizes across 768px
// mid-modal we'd otherwise unmount the Vaul drawer and remount as a
// desktop dialog (or vice versa), which loses scroll position and feels
// like a glitch. Whichever presentation matched at open-time stays for
// this modal's lifetime; the next modal they open re-reads the viewport.
function useIsMobile() {
    const [isMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
    });
    return isMobile;
}

/**
 * API-compatible drop-in replacement for the previous Modal.
 *   <Modal title onClose width>{children}</Modal>
 *
 * Mobile: Vaul drawer (bottom sheet, drag-to-dismiss, iOS-style spring).
 * Desktop: animated dialog (overlay fade + content scale 0.96 → 1).
 *
 * Animation principles applied (Emil Kowalski):
 *   - Custom `--ease-out` curve, not the weak built-in `ease-out`
 *   - Asymmetric timing: 280ms enter, 200ms exit (snappy response on close)
 *   - Never animate from `scale(0)` — start at 0.96 + opacity 0
 *   - `transform-origin: center` is correct for modals (NOT origin-aware,
 *     because the modal is not anchored to a trigger)
 *   - ESC key + click-outside dismiss
 *   - Body scroll lock while open
 */
export function Modal({ title, onClose, onBeforeClose, children, width = 'max-w-lg' }) {
    const isMobile = useIsMobile();
    return isMobile
        ? <MobileDrawer title={title} onClose={onClose} onBeforeClose={onBeforeClose}>{children}</MobileDrawer>
        : <DesktopDialog title={title} onClose={onClose} onBeforeClose={onBeforeClose} width={width}>{children}</DesktopDialog>;
}

// ─── Mobile bottom-sheet (Vaul) ──────────────────────────────────────────
function MobileDrawer({ title, onClose, children }) {
    // Vaul wants controlled open state so it can run its own exit animation
    // before we tear down the tree. We start `open=true`, flip to false on
    // dismiss, and wait for Vaul's transition to settle before calling the
    // parent's onClose (which unmounts us).
    const [open, setOpen] = useState(true);
    const handleOpenChange = (next) => {
        if (!next) {
            setOpen(false);
            // Vaul's drawer animation lands within ~300ms; 350 gives slack.
            setTimeout(onClose, 350);
        }
    };
    return (
        <Drawer.Root open={open} onOpenChange={handleOpenChange}>
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-[2px]" />
                <Drawer.Content
                    className="fixed bottom-0 left-0 right-0 z-[101] flex max-h-[92vh] flex-col rounded-t-2xl bg-white shadow-[0_-8px_32px_-8px_rgba(0,0,0,0.15)] outline-none"
                    aria-describedby={undefined}
                    // Form-control popovers (calendar, time slots, combobox
                    // list — ui/popover.jsx) portal to <body>, i.e. OUTSIDE
                    // the drawer. Radix would treat a tap on them as
                    // "outside" and dismiss the whole sheet. Same for Escape.
                    onPointerDownOutside={(e) => { if (e.target?.closest?.('[data-bbm-popover]')) e.preventDefault(); }}
                    onInteractOutside={(e) => { if (e.target?.closest?.('[data-bbm-popover]')) e.preventDefault(); }}
                    onEscapeKeyDown={(e) => { if (document.querySelector('[data-bbm-popover]')) e.preventDefault(); }}
                >
                    {/* Drag handle — visual affordance for swipe-to-dismiss */}
                    <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-slate-200 flex-shrink-0" aria-hidden="true" />
                    <div className="flex items-center justify-between px-6 pt-3 pb-4 flex-shrink-0">
                        <Drawer.Title className="text-lg font-bold text-slate-900">{title}</Drawer.Title>
                        <IconButton
                            label="Close"
                            aria-label="Close"
                            onClick={() => handleOpenChange(false)}
                        >
                            <IconX size={18} />
                        </IconButton>
                    </div>
                    <div className="px-6 pb-[max(env(safe-area-inset-bottom),24px)] overflow-y-auto flex-1">
                        {children}
                    </div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
}

// ─── Desktop centered dialog ─────────────────────────────────────────────
function DesktopDialog({ title, onClose, onBeforeClose, children, width }) {
    // Drives both enter and exit. requestClose() flips this to false, the
    // CSS transition runs, then onClose() fires to unmount us. Without this
    // two-stage close, the modal would simply disappear with no exit anim.
    const [open, setOpen] = useState(false);
    const contentRef = useRef(null);
    // Element that had focus when the modal opened. We restore focus to
    // it when the modal closes — standard a11y pattern. Without this,
    // closing a modal drops focus to <body> and keyboard users have to
    // re-navigate from the start.
    const previouslyFocusedRef = useRef(null);
    // Guard so the focus-on-open effect only runs ONCE per Modal lifetime.
    // Without this, any re-render that keeps `open === true` (e.g. parent
    // state churn from typing into a controlled input inside the modal)
    // would re-trigger the focus effect and yank focus back to the first
    // focusable. We hit this with ClientIntakeModal where typing a single
    // character anywhere in the form re-focused the Name input.
    const hasFocusedRef = useRef(false);

    // Mount → flip on next frame so the transition has a starting state.
    // Two RAFs ensure the browser actually paints the closed state first.
    useEffect(() => {
        const id1 = requestAnimationFrame(() => {
            const id2 = requestAnimationFrame(() => setOpen(true));
            return () => cancelAnimationFrame(id2);
        });
        return () => cancelAnimationFrame(id1);
    }, []);

    const requestClose = useCallback(() => {
        // Guard hook: parents with unsaved-changes dialogs return false to
        // VETO the close (modal stays fully open while their own dialog
        // asks). Without this the exit animation ran before the guard,
        // leaving nothing to "keep editing" (live test 2026-07-06).
        if (typeof onBeforeClose === 'function' && onBeforeClose() === false) return;
        setOpen(false);
        // 200ms matches the exit duration set in inline styles below.
        // Asymmetric: enter 280ms, exit 200ms (snappy response).
        setTimeout(onClose, 200);
    }, [onClose, onBeforeClose]);

    // Ref-mirror of requestClose so the keydown effect below can stay
    // mount-only. If we put `requestClose` directly in the dep array and a
    // caller passes a new `onClose` reference on every render (common —
    // inline `() => setOpen(false)` at the call site), the effect tears
    // down + re-attaches on every parent render. The teardown calls
    // prev.focus() (restoring focus to the trigger button OUTSIDE the
    // modal), which made every keystroke pop the user out of the field
    // they were typing in. Mirror via ref → effect never re-attaches.
    const requestCloseRef = useRef(requestClose);
    useEffect(() => {
        requestCloseRef.current = requestClose;
    }, [requestClose]);

    // ESC dismiss + body scroll lock + Tab focus trap. Mount-only.
    useEffect(() => {
        // Snapshot the trigger so we can restore focus when the modal closes.
        previouslyFocusedRef.current = document.activeElement;

        const onKey = (e) => {
            if (e.key === 'Escape') {
                // If a Google Places autocomplete dropdown is open, ESC is
                // meant for IT (dismiss suggestions), not the modal — live
                // test 2026-07-06 caught typed intake data being nuked this
                // way. Google hides .pac-container via display:none.
                const pacOpen = [...document.querySelectorAll('.pac-container')]
                    .some(el => el.style.display !== 'none' && el.offsetWidth > 0 && el.offsetHeight > 0);
                if (pacOpen) return;
                // Form-control popovers (ui/popover.jsx: calendar, time
                // slots, combobox list) are portaled and handle their own
                // Escape; they mark themselves with data-bbm-popover.
                if (document.querySelector('[data-bbm-popover]')) return;
                requestCloseRef.current();
                return;
            }
            // Focus trap: when Tab/Shift+Tab tries to leave the modal,
            // wrap to the other end. Without this, keyboard users tab out
            // of the dialog and end up navigating the (visually hidden)
            // page behind it.
            if (e.key === 'Tab' && contentRef.current) {
                const focusables = contentRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        const prevOverflow = document.body.style.overflow;
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            // Restore focus to whatever opened the modal. Guard with
            // .focus existence check for elements that have since unmounted.
            const prev = previouslyFocusedRef.current;
            if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
                prev.focus();
            }
        };
    }, []);

    // Move focus into the modal once it's open. We pick the first
    // focusable (often the close button) instead of the close button
    // specifically — sometimes there's a primary input that should
    // receive focus first, and querying the DOM lets that flow naturally.
    //
    // Guarded by hasFocusedRef so this only fires ONCE per Modal lifetime.
    // The effect deps include `open`, which flips false→true on the two-
    // RAF mount sequence. But if a parent ever re-renders us with `open`
    // still truthy after some other state change, we don't want to yank
    // focus back to the first input mid-edit.
    useEffect(() => {
        if (!open || !contentRef.current) return;
        if (hasFocusedRef.current) return;
        hasFocusedRef.current = true;
        const focusables = contentRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
        // Skip the close button — it's the LAST focusable in our markup
        // but the first focusable when tabbing is usually whatever the
        // user wants to interact with. Pick the first non-close-button
        // focusable, fall back to the modal content itself.
        // (The flatpickr autofocus special case is gone with flatpickr —
        // the DatePicker trigger is a plain button that only opens on click.)
        const firstNonClose = Array.from(focusables).find(el =>
            el.getAttribute('aria-label') !== 'Close');
        if (firstNonClose) {
            firstNonClose.focus({ preventScroll: true });
        } else {
            contentRef.current.focus({ preventScroll: true });
        }
    }, [open]);

    const enterTransition = 'opacity var(--duration-modal) var(--ease-out), transform var(--duration-modal) var(--ease-out)';
    const exitTransition = 'opacity 200ms var(--ease-out), transform 200ms var(--ease-out)';

    // Portal to <body> so the modal escapes any transformed ancestor.
    // Without this, opening a Modal from inside another Modal (or inside
    // any element with `transform: scale(...)` for an animation) would
    // make our `position: fixed inset-0` position relative to that
    // ancestor — rendering the modal at the top of the parent's scroll
    // area instead of the viewport. Same fix that solved the lightbox
    // bug in PhotoGallery.
    return createPortal((
        <div
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : undefined}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={requestClose}
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
                style={{
                    opacity: open ? 1 : 0,
                    transition: open ? enterTransition : exitTransition,
                }}
            />
            <div
                ref={contentRef}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                className={`relative bg-white rounded-2xl max-h-[90vh] overflow-y-auto w-full ${width} shadow-[0_20px_40px_-8px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.04)] focus:outline-none`}
                style={{
                    opacity: open ? 1 : 0,
                    // 0.96 is just visible enough to read as "growing into place"
                    // without looking like it's popping in from nothing.
                    transform: open ? 'scale(1)' : 'scale(0.96)',
                    transition: open ? enterTransition : exitTransition,
                    // transform-origin: center is the correct default for modals.
                    // (Popovers and tooltips should be origin-aware, but modals
                    // are not anchored to a trigger — they live in the viewport.)
                }}
            >
                <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
                    <h2 className="text-lg font-bold text-slate-900">{title}</h2>
                    <IconButton
                        label="Close"
                        aria-label="Close"
                        onClick={requestClose}
                    >
                        <IconX size={18} />
                    </IconButton>
                </div>
                <div className="px-6 py-5">{children}</div>
            </div>
        </div>
    ), portalRoot());
}
